import React, { useEffect, useState, createContext, useCallback } from 'react';
import Header from 'components/header/Header';
import WellService from 'business/services/WellService';
import ScoreGroupsService from 'business/services/PlateService';

import {
  EntityStateDTO,
  GeneralSetupDTO,
  ImageStoreDTO,
  IncubationTemperatureDTO,
  MarcoTrainingAndImprovementStatusDTO,
  ResponseType,
  ScoreDefinitionDTO,
  ScoresGroupsDTO,
  SetupTemperatureDTO,
} from 'business/models/Models';
import cloneDeep from 'lodash/cloneDeep';
import _ from 'lodash';
import './GeneralSetup/setup.css';
import { IStyle } from 'Styles';
import { ExperimentTempConfig } from './GeneralSetup/ExperimentTempConfig';
import { ImprovementOptions } from './GeneralSetup/ImprovementOptions';
import ExperimentService from 'business/services/ExperimentService';
import MarcoService from 'business/services/MarcoService';
import { showAlertSnackBar } from 'utils/Global';
import EScoreGroupName from 'enums/EScoreGroupName';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { ImageStores } from './GeneralSetup/ImageStores';
import { WellScoreConfiguration } from './GeneralSetup/WellScoreConfiguration';
import { useRMDispatch } from 'redux/hooks';
import {
  setImageStoreInfo,
  setScoreGroups,
  setScoresDefinition,
} from 'redux/reducers/commonReducer';
import { useFetchSearchTargets } from 'redux/hooks/searchnFilter';
import { KeyboardKeys } from 'constants/';
import { useSaveDiscardButtons } from 'CustomHooks/useSaveDiscardButtons';
import { LoadingModal } from 'components/modal';

export const generalSetupContext = createContext(null);
export const TempConfigContext = createContext(null);
export const SetupDashboard = () => {
  const [scoresGroupsList, setScoresGroupsList] = useState<ScoresGroupsDTO[]>([]);
  const [baseScoresGroupsList, setBaseScoresGroupsList] = useState<ScoresGroupsDTO[]>([]);
  const [deletedScoresGroupsList, setDeletedScoresGroupsList] = useState<
    ScoresGroupsDTO[]
  >([]);
  const [scoresList, setScoresList] = useState<ScoreDefinitionDTO[]>([]);
  const [baseScoresList, setBaseScoresList] = useState<ScoreDefinitionDTO[]>([]);
  const [deletedScoresList, setDeletedScoresList] = useState<ScoreDefinitionDTO[]>([]);
  const [setupTemp, setSetupTemp] = useState<SetupTemperatureDTO[]>([]);
  const [baseSetupTemp, setBaseSetupTemp] = useState<SetupTemperatureDTO[]>([]);
  const [deletedSetupTemp, setDeletedSetupTemp] = useState<SetupTemperatureDTO[]>([]);
  const [incubationTemp, setIncubationTemp] = useState<IncubationTemperatureDTO[]>([]);
  const [baseIncubationTemp, setBaseIncubationTemp] = useState<
    IncubationTemperatureDTO[]
  >([]);
  const [deletedIncubationTemp, setDeletedIncubationTemp] = useState<
    IncubationTemperatureDTO[]
  >([]);
  const [sendStatisticsOptionStatus, setSendStatisticsOptionStatus] =
    useState<boolean>(false);
  const [baseSendStatisticsOptionStatus, setBaseSendStatisticsOptionStatus] =
    useState<boolean>(false);
  const [collectRetrainingImagesStatus, setCollectRetrainingImagesStatus] =
    useState<MarcoTrainingAndImprovementStatusDTO>();
  const [imageStores, setImagesStores] = useState<ImageStoreDTO[]>([]);
  const [baseImageStores, setBaseImagesStores] = useState<ImageStoreDTO[]>([]);
  const [deletedImageStores, setDeletedImagesStores] = useState<ImageStoreDTO[]>([]);
  const dispatch = useRMDispatch();
  const fetchSearchTargets = useFetchSearchTargets();
  const [loading, setLoading] = useState<{ show: boolean; text: string }>({
    show: false,
    text: 'Updating, this process may take a while',
  });

  const fetchSetupTemp = async () => {
    const expService = new ExperimentService();
    const setupTemperatures = await expService.getSetupTemperatures();
    setSetupTemp(cloneDeep([...setupTemperatures]));
    setBaseSetupTemp(cloneDeep([...setupTemperatures]));
  };

  const fetchScoresGroups = useCallback(async (): Promise<void> => {
    const scoreGroupsService = new ScoreGroupsService();
    const data = await scoreGroupsService.getScoreGroupsService(false, true);
    if (data === null) {
      return null;
    }
    dispatch(setScoreGroups(data));
    const filteredData = data.filter((sg) => sg.name !== EScoreGroupName.Interesting);
    setScoresGroupsList(filteredData);
    setBaseScoresGroupsList(filteredData);
  }, []);

  const fetchScores = async (): Promise<void> => {
    const wellService = new WellService();
    const data = await wellService.getWellDropScores();
    if (data === null) {
      return null;
    }
    setScoresList(
      cloneDeep([...data.sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1))]),
    );
    dispatch(setScoresDefinition(cloneDeep([...data])));
    setBaseScoresList(cloneDeep([...data]));
  };

  const fetchIncubationTemp = async () => {
    const expService = new ExperimentService();
    const setupTemperatures = await expService.getIncubationTemperatures();
    setIncubationTemp(cloneDeep([...setupTemperatures]));
    setBaseIncubationTemp(cloneDeep([...setupTemperatures]));
  };

  const fetchSendStatisticsOptionStatus = async () => {
    const wellservice = new WellService();
    const status = await wellservice.getSendStatisticsOptionStatus();
    setSendStatisticsOptionStatus(status.value);
    setBaseSendStatisticsOptionStatus(status.value);
  };

  const fetchMacroTrainingOptionStatus = async () => {
    const marcoService = new MarcoService();
    const response = await marcoService.GetMarcoTrainingAndImprovementStatus();
    const returnValue = new MarcoTrainingAndImprovementStatusDTO({
      autoCollectRetrainingImages: false,
      collectRetrainingImages: response,
    });
    setCollectRetrainingImagesStatus(returnValue);
  };

  const fetchImageScores = useCallback(async () => {
    const wellservice = new WellService();
    const tempImageStores = await wellservice.getImageStoresInfo();
    dispatch(setImageStoreInfo(cloneDeep([...tempImageStores])));
    setImagesStores(cloneDeep([...tempImageStores]));
    setBaseImagesStores(cloneDeep([...tempImageStores]));
  }, [imageStores]);

  useEffect(() => {
    fetchScores();
    fetchScoresGroups();
    fetchSetupTemp();
    fetchIncubationTemp();
    fetchSendStatisticsOptionStatus();
    fetchMacroTrainingOptionStatus();
    fetchImageScores();
    fetchSearchTargets();
  }, []);

  const onSetSetupTemp = (setupTemperatures: SetupTemperatureDTO[]) => {
    const oldDeletedList = [...deletedSetupTemp];
    const newDeletedList = _.differenceWith(
      setupTemp,
      setupTemperatures,
      function (actual: SetupTemperatureDTO, incoming: SetupTemperatureDTO) {
        return actual.id === incoming.id;
      },
    ).filter((temp) => temp.id > 0);
    if (newDeletedList.length > 0) {
      setDeletedSetupTemp([...oldDeletedList, ...newDeletedList]);
    }
    setSetupTemp(setupTemperatures);
  };

  const onSetIncubtionTemp = (incubationTemperatures: IncubationTemperatureDTO[]) => {
    const oldDeletedList = [...deletedIncubationTemp];
    const newDeletedList = _.differenceWith(
      incubationTemp,
      incubationTemperatures,
      function (actual: IncubationTemperatureDTO, incoming: IncubationTemperatureDTO) {
        return actual.id === incoming.id;
      },
    ).filter((temp) => temp.id > 0);
    if (newDeletedList.length > 0) {
      setDeletedIncubationTemp([...oldDeletedList, ...newDeletedList]);
    }
    setIncubationTemp(incubationTemperatures);
  };

  const onSetScoresGroupsList = useCallback(
    (scoreGroupsList: ScoresGroupsDTO[]): void => {
      const oldDeletedScoreGroupsList = [...deletedScoresGroupsList];
      const newDeletedScoreGroups = _.differenceWith(
        scoresGroupsList,
        scoreGroupsList,
        function (o1, o2) {
          return o1.scoreGroupsId === o2.scoreGroupsId;
        },
      );

      if (newDeletedScoreGroups.length > 0) {
        newDeletedScoreGroups.map((scoreGroup: { scoreGroupsId: number }) => {
          const deletedScores = scoresList.filter(
            (score) =>
              score.wellDropScoreGroupId === scoreGroup.scoreGroupsId &&
              score.wellDropScoreId > 0,
          );
          const updatedScores = scoresList.filter(
            (score) => score.wellDropScoreGroupId !== scoreGroup.scoreGroupsId,
          );
          if (updatedScores.length > 0) {
            setScoresList(updatedScores);
          }
          if (deletedScores.length > 0) {
            setDeletedScoresList(_.concat(deletedScoresList, deletedScores));
          }
        });
        setDeletedScoresGroupsList(
          _.concat(oldDeletedScoreGroupsList, newDeletedScoreGroups),
        );
      }
      setScoresGroupsList(scoreGroupsList);
    },
    [scoresList, scoresGroupsList, deletedScoresGroupsList],
  );

  const onSetScoresList = useCallback(
    (scores: ScoreDefinitionDTO[]): void => {
      const oldDeletedScoresList = [...deletedScoresList];
      const newDeletedScores = _.differenceWith(
        scoresList,
        scores,
        function (oldScore, updatedScore) {
          return oldScore.wellDropScoreId === updatedScore.wellDropScoreId;
        },
      );
      if (newDeletedScores.length > 0) {
        setDeletedScoresList([
          ...oldDeletedScoresList,
          ...newDeletedScores.filter((score) => score.wellDropScoreId > 0),
        ]);
      }
      setScoresList(cloneDeep([...scores]));
    },
    [scoresList, deletedScoresList],
  );
  const validateWellScores = (scores) => {
    let count = 1;
    const errorList = [];
    let errorMsg = '';
    if (scores.some((tempScore) => tempScore.scoreName === '') === true) {
      errorMsg = "(Well Score) 'Name' is a required field.";
      if (isErrorExist(errorList, errorMsg) === false) {
        errorList.push(`${count++}- ${errorMsg}`);
      }
    }
    if (
      scores.some(
        (tempScore) => tempScore.isAutoScore !== true && tempScore.hotKey === '',
      ) === true
    ) {
      errorMsg = '[Well Scores] HotKeys must be specified.\n';
      if (isErrorExist(errorList, errorMsg) === false) {
        errorList.push(`${count++}- ${errorMsg}`);
      }
    }
    scores.map((score: ScoreDefinitionDTO) => {
      if (
        scores.some(
          (tempScore) =>
            tempScore.wellDropScoreGroupId === score.wellDropScoreGroupId &&
            tempScore.wellDropScoreId !== score.wellDropScoreId &&
            tempScore.scoreName === score.scoreName,
        ) === true
      ) {
        errorMsg = '[Well Scores] Name must be unique in a Score Group.\n';
        if (isErrorExist(errorList, errorMsg) === false) {
          errorList.push(`${count++}- ${errorMsg}`);
        }
      }

      if (
        scores.some(
          (tempScore) =>
            tempScore.wellDropScoreId !== score.wellDropScoreId &&
            tempScore.isAutoScore !== true &&
            score.isAutoScore !== true &&
            tempScore.hotKey === score.hotKey,
        ) === true
      ) {
        errorMsg = '[Well Scores] HotKeys must be unique.\n';
        if (isErrorExist(errorList, errorMsg) === false) {
          errorList.push(`${count++}- ${errorMsg}`);
        }
      }
      if (score.hotKey && !score.hotKey.indexOf(' ')) {
        errorMsg = '[Well Scores] HotKey must not contain whitespace.\n';
        if (isErrorExist(errorList, errorMsg) === false) {
          errorList.push(`${count++}- ${errorMsg}`);
        }
      }
      if (score.hotKey.length > 1) {
        errorMsg = '[Well Scores] HotKeys must be one character only.\n';
        if (isErrorExist(errorList, errorMsg) === false) {
          errorList.push(`${count++}- ${errorMsg}`);
        }
      }
      score.hotKey = score.hotKey.replace("'", "''");
      if (
        (score.hotKey.length === 2 && score.hotKey[0] === score.hotKey[1]) ||
        (score.hotKey.length > 0 && score.hotKey.indexOf(score.hotKey[0]) > 0)
      ) {
        errorMsg = '[Well Scores] Hotkey characters conflict with another hot key.\n';
        if (isErrorExist(errorList, errorMsg) === false) {
          errorList.push(`${count++}- ${errorMsg}`);
        }
      }
    });
    if (errorList.length) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, errorList.join(''));
    }
    return errorList.length === 0;
  };
  const isErrorExist = useCallback((errorList, error) => {
    return errorList.length > 0
      ? errorList.some((err) => err.substr(3) === error)
      : false;
  }, []);

  const saveGeneralSettings = useCallback(async () => {
    const wellservice = new WellService();
    const scoreGroups = scoresGroupsList.filter(
      (scoreGroup) =>
        scoreGroup.entityState == EntityStateDTO.Modified ||
        scoreGroup.entityState == EntityStateDTO.New,
    );
    const scores = scoresList.filter(
      (score) =>
        score.entityState == EntityStateDTO.Modified ||
        score.entityState == EntityStateDTO.New,
    );
    if (validateWellScores(scoresList) === false) {
      return;
    }
    setLoading((prev) => ({ ...prev, show: true }));
    const setupTemperature = setupTemp.filter(
      (setup) =>
        setup.entityState == EntityStateDTO.Modified ||
        setup.entityState == EntityStateDTO.New,
    );
    const incubationTemperature = incubationTemp.filter(
      (setup) =>
        setup.entityState == EntityStateDTO.Modified ||
        setup.entityState == EntityStateDTO.New,
    );
    if (validateImageStores(imageStores) === false) {
      return;
    }
    const tempImageStores = imageStores.filter(
      (store) =>
        store.entityState == EntityStateDTO.Modified ||
        store.entityState == EntityStateDTO.New,
    );
    const generalSetup = new GeneralSetupDTO();
    generalSetup.deletedScoreGroups = [...deletedScoresGroupsList];
    generalSetup.deletedScores = [...deletedScoresList];
    generalSetup.upsertScoreGroups = [...scoreGroups];
    generalSetup.upsertScores = [...scores];
    generalSetup.upsertSetupTemperature = [...setupTemperature];
    generalSetup.deletedSetupTemperature = [...deletedSetupTemp];
    generalSetup.upsertIncubationTemperature = [...incubationTemperature];
    generalSetup.deletedIncubationTemperature = [...deletedIncubationTemp];
    generalSetup.upsertImageStores = [...tempImageStores];
    generalSetup.deletedImageStores = [...deletedImageStores];

    const response = await wellservice.saveGeneralSetupSettings(generalSetup);
    setLoading((prev) => ({ ...prev, show: false }));

    if (response.responseType === ResponseType.Success) {
      setDeletedScoresGroupsList([]);
      setDeletedScoresList([]);
      setDeletedSetupTemp([]);
      setDeletedIncubationTemp([]);
      setDeletedImagesStores([]);
      fetchScoresGroups();
      fetchScores();
      fetchSetupTemp();
      fetchIncubationTemp();
      fetchImageScores();
      showAlertSnackBar(
        EAlertType.SUCCESS,
        ESnackBarPosition.TOP,
        'Your changes have been saved successfully.',
      );
    } else {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, response.message);
    }
  }, [scoresGroupsList, scoresList, setupTemp, incubationTemp, imageStores]);

  const cancelGeneralSettings = useCallback((): void => {
    setScoresList(cloneDeep([...baseScoresList]));
    setScoresGroupsList(cloneDeep([...baseScoresGroupsList]));
    setSetupTemp(cloneDeep([...baseSetupTemp]));
    setIncubationTemp(cloneDeep([...baseIncubationTemp]));
    setImagesStores(cloneDeep([...baseImageStores]));
    setSendStatisticsOptionStatus(sendStatisticsOptionStatus);

    setDeletedScoresGroupsList([]);
    setDeletedScoresList([]);
    setDeletedSetupTemp([]);
    setDeletedIncubationTemp([]);
    setDeletedImagesStores([]);
  }, [
    baseScoresGroupsList,
    baseScoresList,
    baseSetupTemp,
    baseIncubationTemp,
    baseImageStores,
  ]);

  const onSetCollectRetrainingImagesStatus = (
    status: MarcoTrainingAndImprovementStatusDTO,
  ) => {
    setCollectRetrainingImagesStatus(
      new MarcoTrainingAndImprovementStatusDTO({
        autoCollectRetrainingImages: status.autoCollectRetrainingImages,
        collectRetrainingImages: status.collectRetrainingImages,
      }),
    );
    const marcoService = new MarcoService();
    marcoService.SetMarcoTrainingAndImprovementStatus(
      status.collectRetrainingImages,
      status.autoCollectRetrainingImages,
    );
  };

  const onSetImagesStores = (imageStoresDtos: ImageStoreDTO[]) => {
    const oldDeletedList = [...deletedImageStores];
    const newDeletedList = _.differenceWith(
      imageStores,
      imageStoresDtos,
      function (oldStore, updatedStore) {
        return oldStore.id === updatedStore.id;
      },
    );
    if (newDeletedList.length > 0) {
      setDeletedImagesStores([...oldDeletedList, ...newDeletedList]);
    }
    setImagesStores(imageStoresDtos);
  };

  const onDeepClone = useCallback((list: any) => {
    return cloneDeep([...list]).map((score: { entityState: EntityStateDTO }) => {
      score.entityState = EntityStateDTO.UnChanged;
      return score;
    });
  }, []);

  const tempScores = onDeepClone(scoresList);
  const tempBaseScores = onDeepClone(baseScoresList);
  const tempBaseScoreGroups = onDeepClone(baseScoresGroupsList);
  const tempScoreGroups = onDeepClone(scoresGroupsList);
  const tempSetupTemper = onDeepClone(setupTemp);
  const tempBaseSetupTemp = onDeepClone(baseSetupTemp);
  const tempIncubationTemper = onDeepClone(incubationTemp);
  const tempBaseIncubationTemp = onDeepClone(baseIncubationTemp);
  const tempImageStores = onDeepClone(imageStores);
  const tempBaseImageStores = onDeepClone(baseImageStores);

  const isEqualScores = _.isEqual(tempBaseScores, tempScores);
  const isEqualGroupScores = _.isEqual(tempBaseScoreGroups, tempScoreGroups);
  const isEqualSetupTemp = _.isEqual(tempBaseSetupTemp, tempSetupTemper);
  const isEqualIncubationTemp = _.isEqual(tempBaseIncubationTemp, tempIncubationTemper);
  const isEqualSendStatisticsOptionStatus = _.isEqual(
    baseSendStatisticsOptionStatus,
    sendStatisticsOptionStatus,
  );
  const isEqualImageStores = _.isEqual(tempBaseImageStores, tempImageStores);
  const saveDiscardButtons = useSaveDiscardButtons({
    enableButtons: !(
      isEqualScores &&
      isEqualGroupScores &&
      isEqualSetupTemp &&
      isEqualIncubationTemp &&
      isEqualSendStatisticsOptionStatus &&
      isEqualImageStores
    ),
    onSave: saveGeneralSettings,
    onDiscard: cancelGeneralSettings,
  });

  const validateImageStores = (imageStores: ImageStoreDTO[]): boolean => {
    let isValid = true;
    let msg = '';
    imageStores.forEach((store) => {
      if (!store.name) {
        msg += "[Image Store]: 'Name' is Required field\n";
        isValid = false;
      }
    });
    if (!isValid) {
      showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, msg);
    }
    return isValid;
  };

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (
          !(
            isEqualScores &&
            isEqualGroupScores &&
            isEqualSetupTemp &&
            isEqualIncubationTemp &&
            isEqualSendStatisticsOptionStatus &&
            isEqualImageStores
          )
        ) {
          saveGeneralSettings();
        }
      }
    },
    [
      isEqualScores,
      isEqualGroupScores,
      isEqualSetupTemp,
      isEqualIncubationTemp,
      isEqualSendStatisticsOptionStatus,
      isEqualImageStores,
      saveGeneralSettings,
    ],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  return (
    <div id="setupdashboard-wrapper" className="custom-tabs">
      <div id="setupdashboard-container" className="setup-bg-topbar">
        <Header
          containerStyle={styles.header}
          headerButtons={saveDiscardButtons}
          isNewTheme={true}
          title={'General'}
        />
        <div
          id="setupdashboard-main"
          className="h-[calc(99vh-85px)] flex flex-row flex-nowrap flex-1 min-h-0 float-left bg-white"
        >
          <div id="setupdashboard-content">
            <generalSetupContext.Provider
              value={{
                scoresGroupsList,
                setScoresGroupsList: onSetScoresGroupsList,
                scoresList,
                setScoresList: onSetScoresList,
              }}
            >
              <WellScoreConfiguration />
            </generalSetupContext.Provider>
            <TempConfigContext.Provider
              value={{
                setupTemp,
                setSetupTemp: onSetSetupTemp,
                incubationTemp,
                setIncubationTemp: onSetIncubtionTemp,
              }}
            >
              <ExperimentTempConfig></ExperimentTempConfig>
            </TempConfigContext.Provider>
            <ImprovementOptions
              sendStatisticsOptionStatus={sendStatisticsOptionStatus}
              setSendStatisticsOptionStatus={setSendStatisticsOptionStatus}
              collectRetrainingImagesStatus={collectRetrainingImagesStatus}
              setCollectRetrainingImagesStatus={onSetCollectRetrainingImagesStatus}
            ></ImprovementOptions>
            <ImageStores
              imageStores={imageStores}
              setImagesStores={onSetImagesStores}
              fetchImageScores={fetchImageScores}
            ></ImageStores>
          </div>
        </div>
      </div>
      <LoadingModal showModal={loading.show} text={loading.text} />
    </div>
  );
};
const styles: IStyle = {
  root: {
    width: '100%',
    padding: 10,
    display: 'flex',
    gap: 10,
    flexDirection: 'column',
  },
  header: {
    marginTop: -6,
  },
};
export default SetupDashboard;
